/**
 * Number formatting of the design system (brainux-design-system.html › Typography): money results
 * always carry a sign with the true minus (U+2212), `$` before the number, thousands separators,
 * abbreviations (K, M) only in KPI headlines, prices keep the symbol's own digits. Numbers are
 * formatted with Latin digits in every language (they stay in the mono family, left to right).
 */
export const MINUS = '−';
const nf = (d: number) => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d });
const cache = new Map<number, Intl.NumberFormat>();
function fixed(v: number, d: number) {
  let f = cache.get(d);
  if (!f) {
    f = nf(d);
    cache.set(d, f);
  }
  return f.format(v);
}
const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

export const DASH = '—';

/** $1,234 · +$1,234 (sign) · −$1,234 */
export function money(v: number | null | undefined, opts: { sign?: boolean; digits?: number; cur?: string } = {}): string {
  if (!isNum(v)) return DASH;
  const { sign = false, digits = 0, cur = '$' } = opts;
  const s = v < 0 ? MINUS : sign && v > 0 ? '+' : '';
  return s + cur + fixed(Math.abs(v), digits);
}

/** Mark of an amount in an account's own currency, for `money`: `$` for USD, else the code ("EUR 1,204.50"). */
export const curMark = (code: string | null | undefined) => (!code || code === 'USD' ? '$' : code + ' ');

/** KPI headline: $182.4K, $12.4M, +$48,210 under 10K */
export function moneyCompact(v: number | null | undefined, opts: { sign?: boolean; cur?: string } = {}): string {
  if (!isNum(v)) return DASH;
  const { sign = false, cur = '$' } = opts;
  const a = Math.abs(v);
  const s = v < 0 ? MINUS : sign && v > 0 ? '+' : '';
  if (a >= 1e9) return s + cur + fixed(a / 1e9, 1) + 'B';
  if (a >= 1e6) return s + cur + fixed(a / 1e6, 1) + 'M';
  if (a >= 1e4) return s + cur + fixed(a / 1e3, 1) + 'K';
  return s + cur + fixed(a, 0);
}

/** plain number with thousands separators */
export function num(v: number | null | undefined, digits = 0, opts: { sign?: boolean } = {}): string {
  if (!isNum(v)) return DASH;
  const s = v < 0 ? MINUS : opts.sign && v > 0 ? '+' : '';
  return s + fixed(Math.abs(v), digits);
}

/** lots: 1.50 · −84.2 */
export function lots(v: number | null | undefined, digits = 2, opts: { sign?: boolean } = {}): string {
  return num(v, digits, opts);
}

/** percent: 68% · 12.4% · +3.1% */
export function pct(v: number | null | undefined, digits = 0, opts: { sign?: boolean } = {}): string {
  if (!isNum(v)) return DASH;
  return num(v, digits, opts) + '%';
}

/** a price with the symbol's digits (no sign, no separators beyond thousands) */
export function price(v: number | null | undefined, digits = 2): string {
  if (!isNum(v)) return DASH;
  return fixed(v, digits);
}

/** compact count: 1,284 · 12.4K */
export function count(v: number | null | undefined): string {
  if (!isNum(v)) return DASH;
  return Math.abs(v) >= 1e5 ? fixed(v / 1e3, 1) + 'K' : fixed(v, 0);
}

/** tone of a signed money result for the design tokens */
export const signTone = (v: number | null | undefined): 'profit' | 'loss' | 'muted' =>
  !isNum(v) || v === 0 ? 'muted' : v > 0 ? 'profit' : 'loss';
export const signColor = (v: number | null | undefined): string =>
  !isNum(v) || v === 0 ? 'var(--muted)' : v > 0 ? 'var(--profit)' : 'var(--loss)';

/** time of day HH:MM:SS (24 h) in a time zone (default: the viewer's) */
export function clock(iso: string | number | Date | null | undefined, timeZone?: string, seconds = true): string {
  if (iso == null) return DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return DASH;
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: seconds ? '2-digit' : undefined,
    hour12: false,
    timeZone,
  }).format(d);
}

type DateInput = string | number | Date | null | undefined;

// Language of dates and units, set by the i18n provider. English keeps the design's forms; other
// languages use their own month names and calendar (languages.calendar: Persian shows solar dates).
// Digits stay Latin in every language.
let dateLang = { code: 'en', calendar: 'gregorian', dir: 'ltr' };
const dateFmt = new Map<string, Intl.DateTimeFormat>();
const CALENDAR_TAG: Record<string, string> = { gregorian: 'gregory', persian: 'persian', islamic: 'islamic' };

export function setFormatLanguage(code: string, calendar = 'gregorian', dir = 'ltr') {
  dateLang = { code, calendar, dir };
  dateFmt.clear();
}
const isEnglish = () => dateLang.code === 'en';
const numberTag = () => dateLang.code + '-u-nu-latn';
const dateTag = () => (isEnglish() ? 'en-GB' : dateLang.code + '-u-ca-' + (CALENDAR_TAG[dateLang.calendar] || 'gregory') + '-nu-latn');

function partsOf(d: Date, o: Intl.DateTimeFormatOptions, timeZone?: string, tag = dateTag()) {
  const key = tag + JSON.stringify(o) + (timeZone || '');
  let f = dateFmt.get(key);
  if (!f) dateFmt.set(key, (f = new Intl.DateTimeFormat(tag, { ...o, timeZone })));
  const p = f.formatToParts(d);
  return (type: Intl.DateTimeFormatPartTypes) => p.find((x) => x.type === type)?.value || '';
}
function validDate(v: DateInput): Date | null {
  if (v == null) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
/** en-US in English: the design writes "Sep", current en-GB data prints "Sept". */
const monthOf = (d: Date, timeZone?: string) =>
  isEnglish() ? partsOf(d, { month: 'short' }, timeZone, 'en-US')('month') : partsOf(d, { month: 'long' }, timeZone)('month');

/**
 * Month name of the active language and calendar ("Sep"). Assemble dates with `date`, `dayMonth` or
 * `monthYear` instead of this next to `getUTCDate()`: the Persian day and year differ.
 */
export function shortMonth(d: Date, timeZone = 'UTC'): string {
  return monthOf(d, timeZone);
}

/** "a, b and c" (or "a, b or c") in the language's own form: Arabic attaches its `و` to the next word */
export function list(items: string[], kind: 'and' | 'or' = 'and'): string {
  return new Intl.ListFormat(isEnglish() ? 'en-GB' : numberTag(), {
    style: 'long',
    type: kind === 'and' ? 'conjunction' : 'disjunction',
  }).format(items);
}

const isRtl = () => dateLang.dir === 'rtl';
/** A text from `t()` going back into a field or the API: without the marks that keep its values in direction. */
export const plainValue = (s: string) => s.replace(/[\u2068\u2069]/g, '');

/** A build version as people read it: "0.1.0+dc4965e624e8929f…" keeps the first 8 characters of the hash. */
export const shortVersion = (v: string | null | undefined) => (v ? v.replace(/\+([0-9a-f]{8})[0-9a-f]+/i, '+$1') : '');

/** The language's comma, for values listed with no closing "and" (`list` adds one). */
export const comma = () => (isRtl() ? '، ' : ', ');
export const commas = (items: (string | number)[]) => items.join(comma());
/** Clauses separated by the language's semicolon. */
export const semicolons = (items: string[]) => items.join(isRtl() ? '؛ ' : '; ');
/** "→" as a sentence reads it: it points left in right-to-left text. */
export const arrow = () => (isRtl() ? '←' : '→');

/** date like "02 Sep 2026" (`pad: false` → "2 Sep 2026") */
export function date(iso: DateInput, timeZone?: string, pad = true): string {
  const d = validDate(iso);
  if (!d) return DASH;
  const g = partsOf(d, { day: pad ? '2-digit' : 'numeric', year: 'numeric' }, timeZone);
  return `${g('day')} ${monthOf(d, timeZone)} ${g('year')}`;
}

/** date like "02 Sep" (`pad: false` → "2 Sep") */
export function dayMonth(iso: DateInput, timeZone?: string, pad = true): string {
  const d = validDate(iso);
  if (!d) return DASH;
  return `${partsOf(d, { day: pad ? '2-digit' : 'numeric' }, timeZone)('day')} ${monthOf(d, timeZone)}`;
}

/** month and year like "Sep 2026" */
export function monthYear(iso: DateInput, timeZone?: string): string {
  const d = validDate(iso);
  if (!d) return DASH;
  return `${monthOf(d, timeZone)} ${partsOf(d, { year: 'numeric' }, timeZone)('year')}`;
}

/** full month and year like "October 2026" */
export function monthLong(iso: DateInput, timeZone?: string): string {
  const d = validDate(iso);
  if (!d) return DASH;
  const g = partsOf(d, { month: 'long', year: 'numeric' }, timeZone);
  return `${g('month')} ${g('year')}`;
}

/** full month like "October" */
export function monthName(iso: DateInput, timeZone?: string): string {
  const d = validDate(iso);
  return d ? partsOf(d, { month: 'long', year: 'numeric' }, timeZone)('month') : DASH;
}

/** weekday like "Fri" */
export function weekday(iso: DateInput, timeZone?: string): string {
  const d = validDate(iso);
  return d ? partsOf(d, { weekday: 'short' }, timeZone)('weekday') : DASH;
}

/** day of the month like "23" (`pad: true` → "03"), for calendar tiles */
export function dayOfMonth(iso: DateInput, timeZone?: string, pad = false): string {
  const d = validDate(iso);
  return d ? partsOf(d, { day: pad ? '2-digit' : 'numeric' }, timeZone)('day') : DASH;
}

/** date range like "1–23 Oct", or "29 Sep – 2 Oct" across months */
export function dayRange(from: DateInput, to: DateInput, timeZone?: string): string {
  const a = validDate(from);
  const b = validDate(to);
  if (!a || !b) return DASH;
  if (monthYear(a, timeZone) === monthYear(b, timeZone)) return `${dayOfMonth(a, timeZone)}–${dayMonth(b, timeZone, false)}`;
  return `${dayMonth(a, timeZone, false)} – ${dayMonth(b, timeZone, false)}`;
}

/** date like "Fri 23 Oct 2026" */
export function day(iso: DateInput, timeZone?: string): string {
  const d = validDate(iso);
  if (!d) return DASH;
  const g = partsOf(d, { weekday: 'short', day: 'numeric', year: 'numeric' }, timeZone);
  return `${g('weekday')} ${g('day')} ${monthOf(d, timeZone)} ${g('year')}`;
}

/** short date-time "23 Oct 14:32" */
export function dateTime(iso: DateInput, timeZone?: string): string {
  const d = validDate(iso);
  if (!d) return DASH;
  const g = partsOf(d, { day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, timeZone);
  return `${g('day')} ${monthOf(d, timeZone)} ${g('hour')}:${g('minute')}`;
}

/** relative age: "14s ago", "3m ago", "2h ago", "5d ago" (the language's own form outside English) */
export function ago(iso: DateInput, now = Date.now()): string {
  if (iso == null) return DASH;
  const s = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  const [n, unit, short]: [number, Intl.RelativeTimeFormatUnit, string] =
    s < 60
      ? [s, 'second', 's']
      : s < 3600
        ? [Math.floor(s / 60), 'minute', 'm']
        : s < 86400
          ? [Math.floor(s / 3600), 'hour', 'h']
          : [Math.floor(s / 86400), 'day', 'd'];
  if (isEnglish()) return n + short + ' ago';
  return new Intl.RelativeTimeFormat(numberTag(), { style: 'short', numeric: 'always' }).format(-n, unit);
}

/** `n` in a unit of the language: "3 ms", "2 دقیقه" (English callers keep their short forms) */
export function unit(n: number, name: 'second' | 'minute' | 'hour' | 'day' | 'millisecond' | 'kilometer-per-hour' | 'kilometer'): string {
  return new Intl.NumberFormat(numberTag(), { style: 'unit', unit: name, unitDisplay: 'short' }).format(n);
}

const LETTER = { second: 's', minute: 'm', hour: 'h', day: 'd' } as const;

/** A short duration: "38s", "2m", "1.5h" in English, the language's own unit elsewhere. */
export function shortUnit(n: number, name: keyof typeof LETTER, digits?: number): string {
  if (isEnglish()) return (digits == null ? String(n) : n.toFixed(digits)) + LETTER[name];
  return unit(digits == null ? n : Number(n.toFixed(digits)), name);
}

/** Age for feeds and drawers: "14s", "3m", "2h", "5d". */
export function age(iso: string | null | undefined): string {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (s < 60) return shortUnit(s, 'second');
  if (s < 3600) return shortUnit(Math.floor(s / 60), 'minute');
  if (s < 86400) return shortUnit(Math.floor(s / 3600), 'hour');
  return shortUnit(Math.floor(s / 86400), 'day');
}

const HORIZON_UNIT = { ms: 'millisecond', s: 'second', m: 'minute', h: 'hour' } as const;

/** A markout horizon as the API sends it ("250ms", "1s", "5m"), in the language's own unit. */
export function horizonLabel(h: string): string {
  const m = /^(\d+)(ms|s|m|h)$/.exec(h);
  return !m || isEnglish() ? h : unit(Number(m[1]), HORIZON_UNIT[m[2] as keyof typeof HORIZON_UNIT]);
}

/** duration from seconds: "38s", "2m 41s", "1h 02m", "3d 4h" (the language's own units outside English) */
export function duration(sec: number | null | undefined): string {
  if (!isNum(sec)) return DASH;
  const s = Math.round(Math.abs(sec));
  if (!isEnglish()) {
    if (s < 60) return unit(s, 'second');
    if (s < 3600) return unit(Math.floor(s / 60), 'minute') + ' ' + unit(s % 60, 'second');
    if (s < 86400) return unit(Math.floor(s / 3600), 'hour') + ' ' + unit(Math.floor((s % 3600) / 60), 'minute');
    return unit(Math.floor(s / 86400), 'day') + ' ' + unit(Math.floor((s % 86400) / 3600), 'hour');
  }
  if (s < 60) return s + 's';
  if (s < 3600) return Math.floor(s / 60) + 'm ' + String(s % 60).padStart(2, '0') + 's';
  if (s < 86400) return Math.floor(s / 3600) + 'h ' + String(Math.floor((s % 3600) / 60)).padStart(2, '0') + 'm';
  return Math.floor(s / 86400) + 'd ' + Math.floor((s % 86400) / 3600) + 'h';
}
